import { useEffect, useRef, useState } from "react";

/** Lanceur d'applications orense (grille 9 points, façon suite Google).
 *  Placé dans la zone droite des barres, près du compte/thème → ne se confond pas
 *  avec la navigation locale (le problème d'un logo « retour » à gauche). Permet de
 *  passer d'un outil à l'autre + revenir au portail, sans repasser par l'accueil. */

const APPS: { name: string; url: string; host: string; icon: JSX.Element; logoUrl?: string }[] = [
  {
    name: "Portail", url: "https://orense.ch", host: "orense.ch",
    icon: <></>, // non utilisé — logoUrl prend le dessus
    logoUrl: "/logo.svg", // emblème vendorisé (public/logo.svg) — pas de dépendance runtime à orense.ch
  },
  {
    name: "OSINT", url: "https://osint.orense.ch", host: "osint.orense.ch",
    icon: <g><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></g>,
  },
  {
    name: "Blindtest", url: "https://blindtest.orense.ch", host: "blindtest.orense.ch",
    icon: <g><path d="M9 18V5l11-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="17" cy="16" r="3" /></g>,
  },
  {
    name: "Python", url: "https://python.orense.ch", host: "python.orense.ch",
    icon: <g><path d="m8 8 4 4-4 4" /><path d="M14 16h3.5" /></g>,
  },
];

export default function AppLauncher() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Exclusivité : à l'ouverture, on signale aux autres menus de la barre (compte) de
  // se fermer → jamais deux panneaux ouverts qui se superposent.
  useEffect(() => {
    if (open) document.dispatchEvent(new CustomEvent("orense:menu-open", { detail: "applauncher" }));
  }, [open]);
  useEffect(() => {
    const onOther = (e: Event) => { if ((e as CustomEvent).detail !== "applauncher") setOpen(false); };
    document.addEventListener("orense:menu-open", onOther);
    return () => document.removeEventListener("orense:menu-open", onOther);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const here = typeof window !== "undefined" ? window.location.host : "";

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <style>{`.pl-app-tile:hover{background:var(--bg-base);}`}</style>
      <button type="button" onClick={() => setOpen((o) => !o)} style={styles.trigger}
              title="Applications orense" aria-label="Applications orense"
              aria-haspopup="menu" aria-expanded={open}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          {[5, 12, 19].flatMap((cy) => [5, 12, 19].map((cx) => (
            <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="2" />
          )))}
        </svg>
      </button>

      {open && (
        <div role="menu" style={styles.panel}>
          <div style={styles.title}>Applications orense</div>
          <div style={styles.grid}>
            {APPS.map((a) => {
              const current = a.host === here;
              return (
                <a key={a.host} href={a.url} className="pl-app-tile"
                   style={{ ...styles.tile, ...(current ? styles.tileCurrent : {}) }}
                   title={current ? `${a.name} (vous êtes ici)` : a.name}>
                  {a.logoUrl ? (
                    <img src={a.logoUrl} width="38" height="26" alt="" aria-hidden="true"
                         style={{ objectFit: "contain", display: "block" }} />
                  ) : (
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                         style={{ color: "var(--blue)" }}>
                      {a.icon}
                    </svg>
                  )}
                  <span style={styles.tileName}>{a.name}</span>
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  trigger: {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    background: "transparent", border: "1px solid var(--border)", borderRadius: "var(--radius)",
    color: "var(--text-muted)", cursor: "pointer", padding: 6,
  },
  panel: {
    position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 60,
    background: "var(--bg-panel)", border: "1px solid var(--border)",
    borderRadius: "var(--radius)", boxShadow: "var(--shadow-1)", padding: 10, width: 248,
  },
  title: { fontSize: 12, color: "var(--text-muted)", padding: "2px 6px 8px" },
  grid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4 },
  tile: {
    display: "flex", flexDirection: "column", alignItems: "center", gap: 6,
    padding: "12px 6px", borderRadius: "var(--radius-sm)", textDecoration: "none",
    color: "var(--text)", textAlign: "center",
  },
  tileCurrent: { background: "var(--bg-base)", outline: "1px solid var(--border)" },
  tileName: { fontSize: 12, fontWeight: 600 },
};
